import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Icon } from '@cbrebuild/blocks';
import * as XLSX from 'xlsx';
import * as adminService from '../../services/admin-service';
import { toast } from '../../redux/actions/toasts-actions';
import DealIQLogo from '../../assets/images/svg/loading-icon.svg';
import AdminManageSettingsDataUpload from './admin-manage-settings-data-upload';
import LoadMore from '../../nucleus/load-more/load-more';
import BrokerAutocomplete from '../../components/autocomplete/broker-autocomplete';
import './admin-manage-proxy-access.scss';

// ---------------------------------------------------------------------------
// Template download (client-side, no server round-trip)
// ---------------------------------------------------------------------------
const handleDownloadProxyTemplate = () => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([['User Email', 'Employee ID', 'Proxy Email', 'Proxy Employee ID']]);
  // Set column widths for readability
  ws['!cols'] = [{ wch: 35 }, { wch: 12 }, { wch: 35 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Template');
  XLSX.writeFile(wb, 'proxy_access_template.xlsx');
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const LoadingSpinner = () => (
  <div className="admin-proxy-spinner">
    <img alt="Loading" src={DealIQLogo} />
  </div>
);

const ValidationBadge = ({ status }) => {
  const map = {
    valid:          { label: 'Valid',           cls: 'admin-proxy-badge--valid' },
    invalid:        { label: 'Invalid',         cls: 'admin-proxy-badge--invalid' },
    duplicate:      { label: 'Duplicate',       cls: 'admin-proxy-badge--duplicate' },
    not_found:      { label: 'Not Found',       cls: 'admin-proxy-badge--invalid' },
    already_exists: { label: 'Already Granted', cls: 'admin-proxy-badge--warning' },
  };
  const { label, cls } = map[status] || { label: status, cls: '' };
  return React.createElement('span', { className: `admin-proxy-badge ${cls}` }, label);
};

// ---------------------------------------------------------------------------
// Individual Grant Panel
// ---------------------------------------------------------------------------

const IndividualGrantPanel = ({ onBack }) => {
  const dispatch = useDispatch();
  const [userEmail, setUserEmail]         = useState('');
  const [proxyEmail, setProxyEmail]       = useState('');
  const [userResult, setUserResult]       = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [grantLoading, setGrantLoading]   = useState(false);
  const [revokingIds, setRevokingIds]     = useState(new Set());

  const handleUserSelect = async (broker) => {
    if (!broker) { setUserEmail(''); setUserResult(null); return; }
    const email = broker.work_email || broker.email || '';
    setUserEmail(email);
    if (!email) return;
    setSearchLoading(true);
    setUserResult(null);
    try {
      const res = await adminService.searchAdminProxyUser(email.trim());
      if (res.error) { dispatch(toast(res.error)); }
      else { setUserResult(res); }
    } catch { dispatch(toast('Failed to search user. Please try again.')); }
    finally { setSearchLoading(false); }
  };

  const handleProxySelect = (broker) => {
    if (!broker) { setProxyEmail(''); return; }
    setProxyEmail(broker.work_email || broker.email || '');
  };

  const grantAccess = async () => {
    if (!userResult || !proxyEmail.trim()) return;
    setGrantLoading(true);
    try {
      const res = await adminService.grantAdminProxyAccess(userEmail.trim(), proxyEmail.trim());
      if (res.error) {
        dispatch(toast(res.error));
      } else {
        dispatch(toast(res.message || 'Proxy access granted successfully.'));
        if (res.created !== false) {
          setProxyEmail('');
          const updated = await adminService.searchAdminProxyUser(userEmail.trim());
          if (!updated.error) setUserResult(updated);
        }
      }
    } catch { dispatch(toast('Failed to grant access. Please try again.')); }
    finally { setGrantLoading(false); }
  };

  const revokeAccess = async (proxyAccessId) => {
    if (revokingIds.has(proxyAccessId)) return;
    setRevokingIds(prev => new Set(prev).add(proxyAccessId));
    try {
      const res = await adminService.revokeAdminProxyAccess(proxyAccessId);
      if (res.error) { dispatch(toast(res.error)); }
      else {
        dispatch(toast(res.message || 'Proxy access revoked.'));
        const updated = await adminService.searchAdminProxyUser(userEmail.trim());
        if (!updated.error) setUserResult(updated);
      }
    } catch { dispatch(toast('Failed to revoke access.')); }
    finally { setRevokingIds(prev => { const s = new Set(prev); s.delete(proxyAccessId); return s; }); }
  };

  return (
    <div className="admin-pool-accounts-container">
      <div className="admin-pool-accounts-header">
        <div className="admin-pool-accounts-header-content">
          <div>
            <h1>Grant Individual Proxy Access</h1>
            <p className="admin-pool-accounts-description">
              Search for a user by name or email, then assign a proxy user to access their account.
            </p>
          </div>
          {onBack && (
            <div className="admin-header-actions">
              <Button variant="secondary" className="admin-view-all-button" onClick={onBack}>
                ← Back to Bulk Upload
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="admin-proxy-individual-content">

      <div className="admin-proxy-search-field">
        <label className="admin-proxy-label">Search For User</label>
        <div className="admin-proxy-autocomplete-wrap">
          <BrokerAutocomplete
            onSelect={handleUserSelect}
            placeholder="Search by name or email…"
            initSearchTerm={userEmail}
          />
        </div>
        <p className="admin-proxy-field-hint">Start typing at least 3 characters to search</p>
      </div>

      {searchLoading && <LoadingSpinner />}

      {!searchLoading && !userResult && (
        <div className="admin-empty-user-state">
          <div className="admin-empty-user-illustration">
            {/* Simple SVG illustration, can be replaced with a local asset if desired */}
            <svg width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="60" cy="70" rx="50" ry="8" fill="#F1F5F9"/>
              <circle cx="40" cy="40" r="16" fill="#E0F2FE" stroke="#38BDF8" strokeWidth="2"/>
              <circle cx="80" cy="40" r="16" fill="#DCFCE7" stroke="#22C55E" strokeWidth="2"/>
              <rect x="30" y="60" width="20" height="8" rx="4" fill="#CBD5E1"/>
              <rect x="70" y="60" width="20" height="8" rx="4" fill="#CBD5E1"/>
              <rect x="52" y="32" width="16" height="8" rx="4" fill="#F1F5F9"/>
            </svg>
          </div>
          <div className="admin-empty-user-title">No user selected</div>
          <div className="admin-empty-user-desc">Search and select a user to manage proxy access.</div>
        </div>
      )}

      {userResult && (
        <div className="admin-proxy-user-card">
          <div className="admin-proxy-user-info-singleline">
            <span className="admin-proxy-user-name">{userResult.user.first_name} {userResult.user.last_name}</span>
          </div>

          {userResult.validation_error && (
            <div className="admin-proxy-warning">&#9888; {userResult.validation_error}</div>
          )}

          {!userResult.validation_error && (
            <div className="admin-proxy-grant-section">
              <label className="admin-proxy-label">Add Proxy User</label>
              <div className="admin-proxy-grant-row">
                <div className="admin-proxy-autocomplete-wrap">
                  <BrokerAutocomplete
                    onSelect={handleProxySelect}
                    placeholder="Search by name or email…"
                    initSearchTerm={proxyEmail}
                  />
                </div>
                <Button
                  className="admin-proxy-btn"
                  variant="primary"
                  onClick={grantAccess}
                  disabled={grantLoading || !proxyEmail.trim()}
                >
                  {grantLoading ? 'Granting…' : 'Grant Access'}
                </Button>
              </div>
            </div>
          )}

          <div className="admin-proxy-existing">
            <h5 className="admin-proxy-existing-title">
              Current Proxy Users
              <span className="admin-proxy-existing-pill">{userResult.existing_proxy_users.length}</span>
            </h5>
            {userResult.existing_proxy_users.length === 0 ? (
              <p className="admin-proxy-empty-text">No proxy users assigned yet.</p>
            ) : (
              <div className="admin-voucher-history-card">
                <div className="results-table">
                  <div className="table-header">
                    <div className="header-cell">NAME</div>
                    <div className="header-cell">EMAIL</div>
                    <div className="header-cell">STATUS</div>
                    <div className="header-cell">ACTION</div>
                  </div>
                  {userResult.existing_proxy_users.map((pu, idx) => (
                    <div className="admin-table-row" key={pu.proxy_access_id}>
                      <div className="admin-cell">
                        <span className="admin-cell-bold">{pu.first_name} {pu.last_name}</span>
                      </div>
                      <div className="admin-cell">{pu.email}</div>
                      <div className="admin-cell">
                        <span className={`admin-proxy-status ${pu.is_active ? 'admin-proxy-status--active' : 'admin-proxy-status--inactive'}`}>
                          {pu.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="admin-cell">
                        <Button
                          variant="secondary"
                          className="admin-proxy-btn-sm admin-proxy-btn--revoke"
                          onClick={() => revokeAccess(pu.proxy_access_id)}
                          disabled={revokingIds.has(pu.proxy_access_id)}
                        >{revokingIds.has(pu.proxy_access_id) ? 'Revoking…' : 'Revoke'}</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Proxy Comparison Table  (used by AdminManageSettingsDataUpload for bulk tab)
// ---------------------------------------------------------------------------

const AdminProxyComparisonTable = ({ data, onBulkUpdate, onCancel, loading }) => {
  const [decisions, setDecisions] = useState(() => {
    const d = {};
    data.forEach(r => { d[r.row_number] = r.status === 'valid' ? 'accept' : 'ignore'; });
    return d;
  });

  const validRows   = data.filter(r => r.status === 'valid');
  const alreadyRows = data.filter(r => r.status === 'already_exists');
  const errorRows   = data.filter(r => !['valid', 'already_exists'].includes(r.status));
  const acceptCount = Object.values(decisions).filter(v => v === 'accept').length;

  const handleAcceptAll = () => {
    const d = {};
    data.forEach(r => { d[r.row_number] = r.status === 'valid' ? 'accept' : 'ignore'; });
    setDecisions(d);
  };
  const handleIgnoreAll = () => {
    const d = {};
    data.forEach(r => { d[r.row_number] = 'ignore'; });
    setDecisions(d);
  };

  const handleSubmit = () => {
    const updates = data
      .filter(r => decisions[r.row_number] === 'accept')
      .map(r => ({ row_number: r.row_number, action: 'accept', data: { user_email: r.new_data.user_email, employee_id: r.new_data.employee_id, proxy_email: r.new_data.proxy_email, proxy_employee_id: r.new_data.proxy_employee_id } }));
    onBulkUpdate(updates);
  };

  return (
    <div>
      <div className="admin-comparison-results-header">
        <div>
          <h1>Validation Preview</h1>
          <p>Review results. Only <strong>Valid</strong> rows can be granted proxy access.</p>
        </div>
        <div className="admin-comparison-results-actions">
          <Button onClick={onCancel} variant="secondary" className="admin-view-all-button">Upload New File</Button>
        </div>
      </div>

      <div className="admin-pool-accounts-comparison-section"><div className="admin-pool-accounts-summary"><div className="admin-pool-accounts-summary-stats">
        <div className="admin-pool-accounts-stat">
          <span className="admin-pool-accounts-stat-label">Total Rows:</span>
          <span className="admin-pool-accounts-stat-value">{data.length}</span>
        </div>
        <div className="admin-pool-accounts-stat admin-pool-accounts-stat-new">
          <span className="admin-pool-accounts-stat-label">Valid:</span>
          <span className="admin-pool-accounts-stat-value">{validRows.length}</span>
        </div>
        <div className="admin-pool-accounts-stat admin-pool-accounts-stat-unchanged">
          <span className="admin-pool-accounts-stat-label">Already Granted:</span>
          <span className="admin-pool-accounts-stat-value">{alreadyRows.length}</span>
        </div>
        <div className="admin-pool-accounts-stat admin-pool-accounts-stat-updated">
          <span className="admin-pool-accounts-stat-label">Invalid / Not Found:</span>
          <span className="admin-pool-accounts-stat-value">{errorRows.length}</span>
        </div>
      </div></div></div>

      <div className="admin-pool-comparison-table-wrapper">
        <div className="admin-pool-comparison-actions-header">
          <div className="admin-pool-comparison-filter-toggle">
            <span style={{ fontSize: '14px', color: '#495057' }}>
              To Grant: <strong>{acceptCount}</strong> &nbsp;|&nbsp; Ignored: <strong>{data.length - acceptCount}</strong>
            </span>
          </div>
          <div className="admin-pool-comparison-bulk-actions">
            <Button onClick={handleAcceptAll} variant="primary" size="small">Accept All Valid</Button>
            <Button onClick={handleIgnoreAll} variant="secondary" size="small">Ignore All</Button>
          </div>
        </div>

        <div className="admin-pool-comparison-table-container">
          <table className="admin-pool-comparison-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th>User Email</th>
                <th style={{ width: '110px' }}>Employee ID</th>
                <th>Proxy Email</th>
                <th style={{ width: '120px' }}>Proxy Employee ID</th>
                <th style={{ width: '140px' }}>Status</th>
                <th>Message</th>
                <th style={{ width: '160px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map(r => {
                const isGrantable = r.status === 'valid';
                const decision    = decisions[r.row_number];
                const rowCls      = decision === 'accept' ? 'admin-pool-row-accepted' : '';
                return (
                  <tr key={r.row_number} className={rowCls}>
                    <td>{r.row_number}</td>
                    <td>{r.new_data.user_email}</td>
                    <td style={{ fontSize: '12px', color: '#6b7280' }}>{r.new_data.employee_id || '\u2014'}</td>
                    <td>{r.new_data.proxy_email}</td>
                    <td style={{ fontSize: '12px', color: '#6b7280' }}>{r.new_data.proxy_employee_id || '\u2014'}</td>
                    <td><ValidationBadge status={r.status} /></td>
                    <td style={{ fontSize: '12px', color: '#6b7280' }}>{r.message || '\u2014'}</td>
                    <td>
                      {isGrantable ? (
                        <div className="admin-pool-comparison-action-buttons">
                          <Button
                            size="small"
                            variant={decision === 'accept' ? 'primary' : 'secondary'}
                            onClick={() => setDecisions(d => ({ ...d, [r.row_number]: 'accept' }))}
                            disabled={loading}
                            className={decision === 'accept' ? 'admin-pool-btn-selected' : ''}
                          >{decision === 'accept' ? '\u2713 Grant' : 'Grant'}</Button>
                          <Button
                            size="small"
                            variant={decision === 'ignore' ? 'danger' : 'secondary'}
                            onClick={() => setDecisions(d => ({ ...d, [r.row_number]: 'ignore' }))}
                            disabled={loading}
                          >Skip</Button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>{'\u2014'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="admin-pool-comparison-submit-actions">
          <Button onClick={handleSubmit} disabled={loading || acceptCount === 0} variant="primary">
            Grant Access ({acceptCount})
          </Button>
          <Button onClick={onCancel} disabled={loading} variant="secondary">Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Proxy bulk config  (drives AdminManageSettingsDataUpload for Bulk Upload tab)
// ---------------------------------------------------------------------------

const proxyBulkConfig = {
  title: 'Manage Proxy Access',
  description: 'Upload a CSV or Excel file with columns: User Email, Employee ID, Proxy Email, Proxy Employee ID. Employee ID and email must match the same user. Max 5\u202fMB / 5\u202f000 rows.',
  uploadTitle: 'Upload Proxy Access File',
  entityName: 'proxy access records',
  fileInputId: 'proxy-bulk',

  templateDownload: {
    show: true,
    buttonText: 'Download Template File',
    onDownload: handleDownloadProxyTemplate,
  },

  uploadService: async (file) => {
    const res = await adminService.bulkValidateAdminProxyFile(file);
    if (res.error) throw { error: res.error };
    const s = res.summary || {};
    return {
      total_records:   s.total   || 0,
      new_count:       s.valid   || 0,
      updated_count:   s.already_exists || 0,
      unchanged_count: (s.duplicate || 0) + (s.not_found || 0) + (s.invalid || 0),
      results: (res.records || []).map(r => ({
        row_number:    r.row_number,
        status:        r.status,
        new_data:      {
          user_email:    r.user_email,
          employee_id:       r.employee_id,
          proxy_email:       r.proxy_email,
          proxy_employee_id: r.proxy_employee_id,
        },
        discrepancies: {},
        message:       r.message,
      })),
    };
  },

  bulkUpdateService: async (updates) => {
    const records = updates.map(u => ({
      user_email:    u.data.user_email,
      employee_id:       u.data.employee_id,
      proxy_email:       u.data.proxy_email,
      proxy_employee_id: u.data.proxy_employee_id,
    }));
    if (records.length === 0) {
      return { summary: { created_count: 0, updated_count: 0, ignored_count: 0, error_count: 0 } };
    }
    const res = await adminService.bulkGrantAdminProxyAccess(records);
    if (res.error) return { error: res.error };
    const s = res.summary || {};
    return { summary: { created_count: s.success || 0, updated_count: 0, ignored_count: 0, error_count: s.failed || 0 } };
  },

  getFileInfoService:  null,
  downloadFileService: null,
  idField:             'row_number',
  hideComparisonStats: true,
};

// ---------------------------------------------------------------------------
// View All Records Panel
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

const ViewAllPanel = ({ onBack }) => {
  const dispatch = useDispatch();
  const [groups,       setGroups]       = useState([]); // [{user, proxy_count, proxy_users}]
  const [total,        setTotal]        = useState(0);  // count of distinct users
  const [currentPage,  setCurrentPage]  = useState(1);
  const [search,       setSearch]       = useState('');
  const [searchInput,  setSearchInput]  = useState('');
  const [loading,      setLoading]      = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [expanded,     setExpanded]     = useState(new Set()); // expanded user IDs
  const [revoking,  setRevoking]  = useState(new Set()); // proxy_access_ids being revoked

  const fetchGroups = useCallback(async (pg, srch, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const res = await adminService.fetchAdminProxyAccessList(pg, PAGE_SIZE, srch);
      if (append) {
        setGroups(prev => [...prev, ...(res.results || [])]);
      } else {
        setGroups(res.results || []);
      }
      setTotal(res.count || 0);
    } catch {
      dispatch(toast('Failed to load proxy access records.'));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    setGroups([]);
    setCurrentPage(1);
    setExpanded(new Set());
    fetchGroups(1, search);
  }, [fetchGroups, search]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleLoadMore = () => {
    const next = currentPage + 1;
    setCurrentPage(next);
    fetchGroups(next, search, true);
  };

  const toggleExpand = (userId) => {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(userId) ? s.delete(userId) : s.add(userId);
      return s;
    });
  };

  const handleRevoke = async (proxyAccessId, userId) => {
    // Capture synchronously before any await — groups is current at this point
    const group = groups.find(g => g.user.id === userId);
    const shouldDecrementTotal = group && group.proxy_users.length === 1;

    setRevoking(prev => new Set(prev).add(proxyAccessId));
    try {
      const res = await adminService.revokeAdminProxyAccess(proxyAccessId);
      if (res.error) {
        dispatch(toast(res.error));
      } else {
        dispatch(toast(res.message || 'Proxy access revoked.'));
        // Remove the proxy from the group; remove the whole group row if no proxies left
        setGroups(prev =>
          prev
            .map(g => {
              if (g.user.id !== userId) return g;
              const updated = g.proxy_users.filter(pu => pu.proxy_access_id !== proxyAccessId);
              return { ...g, proxy_users: updated, proxy_count: updated.length };
            })
            .filter(g => g.proxy_count > 0)
        );
        setTotal(prev => shouldDecrementTotal ? prev - 1 : prev);
      }
    } catch {
      dispatch(toast('Failed to revoke access.'));
    } finally {
      setRevoking(prev => { const s = new Set(prev); s.delete(proxyAccessId); return s; });
    }
  };

  const loadedUsers = groups.length;

  return (
    <div className="admin-pool-accounts-container">
      <div className="admin-pool-accounts-header">
        <div className="admin-pool-accounts-header-content">
          <div>
            <h1>All Proxy Access Records</h1>
            <p className="admin-pool-accounts-description">Browse and manage all existing proxy access grants.</p>
          </div>
          <Button variant="secondary" className="admin-view-all-button" onClick={onBack}>
            ← Upload / Grant
          </Button>
        </div>
      </div>

      <div className="admin-voucher-history-card">
        {/* Search + summary */}
        <div className="admin-view-all-header">
          <div className="admin-view-all-search-container">
            <Icon iconName="search" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <div className="admin-view-all-summary">
            {total > 0
              ? `Showing ${loadedUsers} of ${total} users`
              : 'No records found'}
            {search && ` (filtered by "${search}")`}
          </div>
        </div>

      {/* Grouped expandable table */}
      <div className="admin-pool-comparison-table-wrapper admin-view-all-table-wrapper">
        <div className="admin-pool-comparison-table-container">
          {loading ? <LoadingSpinner /> : (
            <table className="admin-pool-comparison-table admin-view-all-table">
              <thead>
                <tr>
                  <th className="admin-view-all-col-expand" />
                  <th>USER (ACCOUNT OWNER)</th>
                  <th>EMAIL</th>
                  <th>PROXY USERS / STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="admin-view-all-table-empty">No proxy access records found.</td>
                  </tr>
                ) : groups.map(group => {
                  const isExpanded = expanded.has(group.user.id);
                  return (
                    <React.Fragment key={group.user.id}>
                      {/* Parent user row */}
                      <tr
                        className="admin-proxy-group-row"
                        onClick={() => toggleExpand(group.user.id)}
                      >
                        <td className="admin-proxy-expand-cell">
                          <span className={`admin-proxy-expand-icon ${isExpanded ? 'admin-proxy-expand-icon--open' : ''}`}>
                            ›
                          </span>
                        </td>
                        <td className="admin-proxy-group-name">
                          {group.user.first_name} {group.user.last_name}
                        </td>
                        <td>{group.user.email}</td>
                        <td>
                          <span className="admin-proxy-proxy-count">
                            {group.proxy_count} proxy user{group.proxy_count !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="admin-proxy-group-action-cell" />
                      </tr>

                      {/* Expanded proxy user rows */}
                      {isExpanded && group.proxy_users.map(pu => (
                        <tr key={pu.proxy_access_id} className="admin-proxy-child-row">
                          <td />
                          <td>{pu.first_name} {pu.last_name}</td>
                          <td>{pu.email}</td>
                          <td>
                            <span className={`admin-proxy-status ${pu.is_active ? 'admin-proxy-status--active' : 'admin-proxy-status--inactive'}`}>
                              {pu.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Button
                              variant="secondary"
                              className="admin-proxy-btn-sm admin-proxy-btn--revoke"
                              onClick={(e) => { e.stopPropagation(); handleRevoke(pu.proxy_access_id, group.user.id); }}
                              disabled={revoking.has(pu.proxy_access_id)}
                            >
                              {revoking.has(pu.proxy_access_id) ? 'Revoking…' : 'Revoke'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <LoadMore
        isLoading={loadingMore}
        hasNextPage={loadedUsers < total}
        handleLoadMore={handleLoadMore}
      />
    </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AdminManageProxyAccess = () => {
  const [showViewAll,     setShowViewAll]     = useState(false);
  const [showIndividual,  setShowIndividual]  = useState(false);

  if (showIndividual) {
    return <IndividualGrantPanel onBack={() => setShowIndividual(false)} />;
  }

  if (showViewAll) {
    return <ViewAllPanel onBack={() => setShowViewAll(false)} />;
  }

  return (
    <AdminManageSettingsDataUpload
      config={proxyBulkConfig}
      ComparisonTableComponent={AdminProxyComparisonTable}
      extraHeaderActions={
        <>
          <Button variant="secondary" className="admin-view-all-button" onClick={() => setShowIndividual(true)}>
            Grant Individual Access
          </Button>
          <Button variant="secondary" className="admin-view-all-button" onClick={() => setShowViewAll(true)}>
            View All Records
          </Button>
        </>
      }
    />
  );
};

export default AdminManageProxyAccess;
